import React, { useRef, useState } from 'react';
import c from 'classnames';
import type { UnregisterCallback } from 'history';
import { history } from 'instantsearch.js/es/lib/routers';
import { useHistory } from 'react-router';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { getSearchFiltersParameter } from '@uncinc/react-searchkit';
import type { SearchParameters } from '@uncinc/react-searchkit/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { SearchTopical, SearchTopicalEvents } from '../../../components';
import { requestOptions } from '../../../components/SearchTopicalArticles/config';
import { APP_OPTIONS } from '../../../config/constants';
import type { GenericParagraphEntity } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

export interface ParagraphTopicalProps {
  entity: ImmutableEntity<GenericParagraphEntity>;
  className?: string;
}

const ParagraphTopical: React.FC<ParagraphTopicalProps> = ({ entity, className }) => {
  const removeHistoryListener = useRef<UnregisterCallback>();
  const routerHistory = useHistory();

  const paragraph = entity.toJS();
  const uuid = paragraph.uuid[0].value;

  const classNames = c('ParagraphTopical', className);

  const [searchParameters, setSearchParameters] = useState<SearchParameters>({ filters: '', page: 0 });

  const routing = {
    router: history({
      start: (onUpdate) => {
        removeHistoryListener.current = routerHistory.listen(onUpdate);
      },
      dispose: () => removeHistoryListener.current,
    }),
  };

  return (
    <div id={uuid} className={classNames}>
      <SearchTopical
        routing={routing}
        requestOptions={requestOptions}
        onStateChange={({ uiState, setUiState }) => {
          const indexUiState = uiState[APP_OPTIONS.parameters.searchkitIndexTopical];

          // Copy over the filters from this search to the events search.
          setSearchParameters((prevSearchParameters) => {
            const filters = getSearchFiltersParameter(indexUiState || {});

            // If the filters haven't changed, we don't want to trigger
            // a search parameter change, so return the previous state.
            // This is also done to prevent unnecessary rerenders of the
            // map, triggered by other uiState changes.
            if (prevSearchParameters.filters === filters) {
              return prevSearchParameters;
            }

            return {
              filters,
              page: 0,
            };
          });

          setUiState(uiState);
        }}
      >
        <Container>
          <div className="row">
            <SearchTopical.Facets className="ParagraphTopical__facets">
              <div className="col-12 mt-12 mt-lg-0 order-2 order-lg-1">
                <SearchTopical.TypeFacet className="ParagraphTopical__type-facet" />
              </div>

              <div className="col-12 col-lg-3 mt-lg-1 order-1 order-lg-2">
                <SearchTopical.AdvancedFacets />

                <div className="mt-2 d-lg-none">
                  <SearchTopical.ActiveFacets />
                </div>
              </div>
            </SearchTopical.Facets>

            <div className="col-12 col-lg-9 mt-12 mt-lg-1 order-3">
              <SearchTopical.Results
                className="u-flex--column u-gap--row--12 u-gap--row--lg--24"
              >
                {({ contentType }) => (
                  <>
                    {/*
                      * Content type can be article, event or undefined. In this case
                      * undefined means we want to show all types. So we just check if
                      * it's not the other type.
                      */}
                    {contentType !== 'article' && (
                      <SearchTopicalEvents searchParameters={searchParameters} />
                    )}

                    {contentType !== 'event' && (
                      <SearchTopical.ResultsArticles />
                    )}
                  </>
                )}
              </SearchTopical.Results>
            </div>
          </div>
        </Container>
      </SearchTopical>
    </div>
  );
};

export default ParagraphTopical;
